import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Users,
  Settings2,
  Loader2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  actualizarCliente,
  actualizarDefinicionCampo,
  crearCliente,
  crearDefinicionCampo,
  eliminarCliente,
  eliminarDefinicionCampo,
  listarClientes,
  listarDefinicionesCampos,
} from "../lib/clientsApi";
import {
  LIMITES,
  sanearCiudad,
  sanearCp,
  sanearNif,
  sanearNombre,
  sanearTelefono,
  validarClienteForm,
} from "../lib/validations";

const FIELD_TYPE_LABEL = {
  string: "Texto corto",
  longtext: "Texto largo",
  number: "Número",
  boolean: "Sí/No",
  date: "Fecha",
  enum: "Opciones",
};

const FIELD_TYPES = Object.keys(FIELD_TYPE_LABEL);

const EMPTY_CLIENT_FORM = {
  nombre: "",
  nif: "",
  email: "",
  telefono: "",
  direccion: "",
  ciudad: "",
  cp: "",
  notas: "",
  customFields: {},
};

function slugifyKey(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
}

export default function Clientes() {
  const [tab, setTab] = useState("lista");
  const [clientes, setClientes] = useState([]);
  const [defs, setDefs] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [busqueda, setBusqueda] = useState("");

  const [clienteEditando, setClienteEditando] = useState(null);
  const [clienteCrear, setClienteCrear] = useState(false);
  const [clienteEliminar, setClienteEliminar] = useState(null);

  const [defCrear, setDefCrear] = useState(false);
  const [defEditando, setDefEditando] = useState(null);
  const [defEliminar, setDefEliminar] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [cs, fs] = await Promise.all([
        listarClientes(),
        listarDefinicionesCampos(),
      ]);
      setClientes(cs);
      setDefs(fs);
    } catch (err) {
      console.error("clientes_load_error", err);
      setError(err?.response?.data?.error || "No se pudieron cargar los clientes");
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return clientes;
    return clientes.filter((c) =>
      [c.nombre, c.nif, c.email, c.telefono, c.ciudad]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [clientes, busqueda]);

  const guardarCliente = async (id, payload) => {
    try {
      if (id) {
        await actualizarCliente(id, payload);
        toast.success("Cliente actualizado");
      } else {
        await crearCliente(payload);
        toast.success("Cliente creado");
      }
      setClienteCrear(false);
      setClienteEditando(null);
      cargar();
    } catch (err) {
      console.error("cliente_save_error", err);
      toast.error(
        err?.response?.data?.error || "No se pudo guardar el cliente",
      );
    }
  };

  const confirmarEliminarCliente = async () => {
    if (!clienteEliminar) return;
    try {
      await eliminarCliente(clienteEliminar.id);
      toast.success("Cliente eliminado");
      setClienteEliminar(null);
      cargar();
    } catch (err) {
      console.error("cliente_delete_error", err);
      toast.error("No se pudo eliminar el cliente");
    }
  };

  const guardarDef = async (keyOriginal, payload) => {
    try {
      if (keyOriginal) {
        await actualizarDefinicionCampo(keyOriginal, payload);
        toast.success("Campo actualizado");
      } else {
        await crearDefinicionCampo(payload);
        toast.success("Campo creado");
      }
      setDefCrear(false);
      setDefEditando(null);
      cargar();
    } catch (err) {
      console.error("def_save_error", err);
      toast.error(
        err?.response?.data?.error || "No se pudo guardar el campo personalizado",
      );
    }
  };

  const confirmarEliminarDef = async () => {
    if (!defEliminar) return;
    try {
      await eliminarDefinicionCampo(defEliminar.key);
      toast.success("Campo eliminado");
      setDefEliminar(null);
      cargar();
    } catch (err) {
      console.error("def_delete_error", err);
      toast.error("No se pudo eliminar el campo");
    }
  };

  return (
    <div className="px-10 py-10 max-w-6xl mx-auto" data-testid="clientes-page">
      <div className="flex items-end justify-between mb-2">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">
            Reformas Express
          </div>
          <h1 className="text-4xl sm:text-5xl font-display font-bold tracking-tight">
            Tus clientes.
          </h1>
          <p className="text-slate-500 mt-2 max-w-xl">
            Gestiona tu cartera de clientes y los campos personalizados que necesites.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList className="bg-white border border-slate-200 rounded-md">
          <TabsTrigger value="lista" className="gap-2" data-testid="tab-clientes">
            <Users className="w-4 h-4" /> Clientes
          </TabsTrigger>
          <TabsTrigger value="campos" className="gap-2" data-testid="tab-campos">
            <Settings2 className="w-4 h-4" /> Campos personalizados
          </TabsTrigger>
        </TabsList>

        <TabsContent value="lista" className="mt-6">
          <div className="rx-card overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-4">
              <h2 className="font-display font-bold text-lg">Listado</h2>
              <div className="flex items-center gap-3">
                <div className="relative w-72 max-w-full">
                  <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                  <Input
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Buscar por nombre, email, NIF…"
                    className="pl-9 h-9"
                    data-testid="clientes-search"
                  />
                </div>
                <Button
                  onClick={() => setClienteCrear(true)}
                  className="bg-blue-600 hover:bg-blue-700 gap-2"
                  data-testid="cliente-nuevo-btn"
                >
                  <Plus className="w-4 h-4" /> Nuevo
                </Button>
              </div>
            </div>

            {cargando ? (
              <CargandoPlaceholder />
            ) : error ? (
              <ErrorPlaceholder error={error} onRetry={cargar} />
            ) : filtrados.length === 0 ? (
              <div className="p-16 text-center" data-testid="clientes-vacio">
                <div className="inline-flex p-4 rounded-full bg-blue-50 text-blue-600 mb-4">
                  <Users className="w-7 h-7" />
                </div>
                <h3 className="font-display font-bold text-xl mb-2">
                  {busqueda ? "Sin resultados" : "Sin clientes aún"}
                </h3>
                <p className="text-slate-500 mb-5 text-sm">
                  {busqueda
                    ? "Prueba con otros términos."
                    : "Empieza añadiendo tu primer cliente."}
                </p>
                {!busqueda && (
                  <Button
                    onClick={() => setClienteCrear(true)}
                    className="bg-blue-600 hover:bg-blue-700 gap-2"
                  >
                    <Plus className="w-4 h-4" /> Crear cliente
                  </Button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filtrados.map((c) => (
                  <div
                    key={c.id}
                    className="px-5 py-4 grid grid-cols-12 gap-4 items-center hover:bg-slate-50/60 transition-colors"
                    data-testid={`cliente-row-${c.id}`}
                  >
                    <div className="col-span-4">
                      <div className="font-mono text-xs text-slate-400">{c.id}</div>
                      <div className="font-semibold text-slate-900 truncate">
                        {c.nombre}
                      </div>
                      {c.nif ? (
                        <div className="text-xs text-slate-500">{c.nif}</div>
                      ) : null}
                    </div>
                    <div className="col-span-3 text-sm text-slate-600 truncate">
                      {c.email || "—"}
                    </div>
                    <div className="col-span-2 text-sm text-slate-600 truncate">
                      {c.telefono || "—"}
                    </div>
                    <div className="col-span-2 text-sm text-slate-500 truncate">
                      {c.ciudad || "—"}
                    </div>
                    <div className="col-span-1 flex justify-end items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => setClienteEditando(c)}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        aria-label="Editar"
                        title="Editar"
                        data-testid={`cliente-editar-${c.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setClienteEliminar(c)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        aria-label="Eliminar"
                        title="Eliminar"
                        data-testid={`cliente-eliminar-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="campos" className="mt-6">
          <div className="rx-card overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-4">
              <div>
                <h2 className="font-display font-bold text-lg">Campos personalizados</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Define campos extra que aparecerán en el formulario de cliente.
                </p>
              </div>
              <Button
                onClick={() => setDefCrear(true)}
                className="bg-blue-600 hover:bg-blue-700 gap-2"
                data-testid="campo-nuevo-btn"
              >
                <Plus className="w-4 h-4" /> Nuevo campo
              </Button>
            </div>
            {cargando ? (
              <CargandoPlaceholder />
            ) : defs.length === 0 ? (
              <div className="p-16 text-center" data-testid="campos-vacio">
                <div className="inline-flex p-4 rounded-full bg-blue-50 text-blue-600 mb-4">
                  <Settings2 className="w-7 h-7" />
                </div>
                <h3 className="font-display font-bold text-xl mb-2">
                  Aún no tienes campos personalizados
                </h3>
                <p className="text-slate-500 mb-5 text-sm">
                  Crea campos específicos para tu negocio (ej: tipo de cliente, descuento, fuente…).
                </p>
                <Button
                  onClick={() => setDefCrear(true)}
                  className="bg-blue-600 hover:bg-blue-700 gap-2"
                >
                  <Plus className="w-4 h-4" /> Crear campo
                </Button>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {defs.map((d) => (
                  <li
                    key={d.key}
                    className="px-5 py-4 flex items-center gap-4"
                    data-testid={`campo-row-${d.key}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900">{d.label}</div>
                      <div className="font-mono text-xs text-slate-500">{d.key}</div>
                    </div>
                    <span className="text-xs px-2 py-1 rounded-md bg-slate-100 text-slate-600">
                      {FIELD_TYPE_LABEL[d.type] ?? d.type}
                    </span>
                    {d.required ? (
                      <span className="text-xs px-2 py-1 rounded-md bg-amber-50 text-amber-700 border border-amber-200">
                        Obligatorio
                      </span>
                    ) : null}
                    <div className="flex items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => setDefEditando(d)}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                        aria-label="Editar campo"
                        data-testid={`campo-editar-${d.key}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDefEliminar(d)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                        aria-label="Eliminar campo"
                        data-testid={`campo-eliminar-${d.key}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <ClienteModal
        open={clienteCrear || !!clienteEditando}
        cliente={clienteEditando}
        defs={defs}
        onClose={() => {
          setClienteCrear(false);
          setClienteEditando(null);
        }}
        onSave={guardarCliente}
      />

      <ConfirmEliminar
        open={!!clienteEliminar}
        title="Eliminar cliente"
        descripcion={`¿Seguro que quieres eliminar a "${clienteEliminar?.nombre || ""}"? Esta acción no se puede deshacer.`}
        onCancel={() => setClienteEliminar(null)}
        onConfirm={confirmarEliminarCliente}
      />

      <DefinicionModal
        open={defCrear || !!defEditando}
        defActual={defEditando}
        onClose={() => {
          setDefCrear(false);
          setDefEditando(null);
        }}
        onSave={guardarDef}
      />

      <ConfirmEliminar
        open={!!defEliminar}
        title="Eliminar campo personalizado"
        descripcion={`¿Eliminar el campo "${defEliminar?.label || ""}"? Se borrará el valor de este campo en todos los clientes.`}
        onCancel={() => setDefEliminar(null)}
        onConfirm={confirmarEliminarDef}
      />
    </div>
  );
}

function CargandoPlaceholder() {
  return (
    <div className="p-16 text-center">
      <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto mb-3" />
      <p className="text-slate-500 text-sm">Cargando…</p>
    </div>
  );
}

function ErrorPlaceholder({ error, onRetry }) {
  return (
    <div className="p-16 text-center">
      <p className="text-red-600 text-sm mb-3">{error}</p>
      <Button onClick={onRetry} variant="outline" className="border-slate-300">
        Reintentar
      </Button>
    </div>
  );
}

function ClienteModal({ open, cliente, defs, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_CLIENT_FORM);
  const [touched, setTouched] = useState({});
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (cliente) {
      setForm({
        nombre: cliente.nombre || "",
        nif: cliente.nif || "",
        email: cliente.email || "",
        telefono: cliente.telefono || "",
        direccion: cliente.direccion || "",
        ciudad: cliente.ciudad || "",
        cp: cliente.cp || "",
        notas: cliente.notas || "",
        customFields: { ...(cliente.customFields || {}) },
      });
    } else {
      setForm(EMPTY_CLIENT_FORM);
    }
    setTouched({});
  }, [open, cliente]);

  const errores = useMemo(() => validarClienteForm(form), [form]);
  const valido = Object.keys(errores).length === 0;

  const marcarTouched = (k) => setTouched((t) => ({ ...t, [k]: true }));
  const errorVisible = (k) =>
    (touched[k] || touched.__submit) ? errores[k] : null;

  const submit = async (e) => {
    e.preventDefault();
    if (enviando) return;
    if (!valido) {
      setTouched((t) => ({ ...t, __submit: true }));
      return;
    }
    setEnviando(true);
    try {
      const payload = {
        ...form,
        nombre: form.nombre.trim(),
        nif: form.nif ? sanearNif(form.nif) : null,
        email: form.email ? form.email.trim().toLowerCase() : null,
        telefono: form.telefono ? form.telefono.trim() : null,
        direccion: form.direccion ? form.direccion.trim() : null,
        ciudad: form.ciudad ? form.ciudad.trim() : null,
        cp: form.cp ? form.cp.trim() : null,
        notas: form.notas ? form.notas.trim() : null,
      };
      await onSave(cliente?.id || null, payload);
    } finally {
      setEnviando(false);
    }
  };

  const setCustom = (key, value) => {
    setForm((f) => ({
      ...f,
      customFields: { ...(f.customFields || {}), [key]: value },
    }));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cliente-modal">
        <DialogHeader>
          <DialogTitle>{cliente ? "Editar cliente" : "Nuevo cliente"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Campo label="Nombre / Razón social *" error={errorVisible("nombre")}>
              <Input
                value={form.nombre}
                onChange={(e) =>
                  setForm({ ...form, nombre: sanearNombre(e.target.value) })
                }
                onBlur={() => marcarTouched("nombre")}
                maxLength={LIMITES.nombreMax}
                aria-invalid={!!errorVisible("nombre")}
                required
                data-testid="cliente-form-nombre"
                autoFocus
              />
            </Campo>
            <Campo label="NIF / CIF" error={errorVisible("nif")}>
              <Input
                value={form.nif}
                onChange={(e) => setForm({ ...form, nif: sanearNif(e.target.value) })}
                onBlur={() => marcarTouched("nif")}
                maxLength={LIMITES.nifMax}
                aria-invalid={!!errorVisible("nif")}
                data-testid="cliente-form-nif"
                placeholder="12345678Z / B12345678"
              />
            </Campo>
            <Campo label="Email" error={errorVisible("email")}>
              <Input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) =>
                  setForm({
                    ...form,
                    email: e.target.value.slice(0, LIMITES.emailMax),
                  })
                }
                onBlur={() => marcarTouched("email")}
                maxLength={LIMITES.emailMax}
                aria-invalid={!!errorVisible("email")}
                data-testid="cliente-form-email"
              />
            </Campo>
            <Campo label="Teléfono" error={errorVisible("telefono")}>
              <Input
                inputMode="tel"
                autoComplete="tel"
                value={form.telefono}
                onChange={(e) =>
                  setForm({ ...form, telefono: sanearTelefono(e.target.value) })
                }
                onBlur={() => marcarTouched("telefono")}
                maxLength={LIMITES.telefonoMax}
                aria-invalid={!!errorVisible("telefono")}
                data-testid="cliente-form-telefono"
                placeholder="+34 612 345 678"
              />
            </Campo>
            <Campo label="Dirección" error={errorVisible("direccion")} full>
              <Input
                value={form.direccion}
                onChange={(e) =>
                  setForm({
                    ...form,
                    direccion: e.target.value.slice(0, LIMITES.direccionMax),
                  })
                }
                onBlur={() => marcarTouched("direccion")}
                maxLength={LIMITES.direccionMax}
                aria-invalid={!!errorVisible("direccion")}
                data-testid="cliente-form-direccion"
              />
            </Campo>
            <Campo label="Ciudad" error={errorVisible("ciudad")}>
              <Input
                value={form.ciudad}
                onChange={(e) =>
                  setForm({ ...form, ciudad: sanearCiudad(e.target.value) })
                }
                onBlur={() => marcarTouched("ciudad")}
                maxLength={LIMITES.ciudadMax}
                aria-invalid={!!errorVisible("ciudad")}
                data-testid="cliente-form-ciudad"
              />
            </Campo>
            <Campo label="C.P." error={errorVisible("cp")}>
              <Input
                inputMode="numeric"
                autoComplete="postal-code"
                value={form.cp}
                onChange={(e) => setForm({ ...form, cp: sanearCp(e.target.value) })}
                onBlur={() => marcarTouched("cp")}
                maxLength={5}
                aria-invalid={!!errorVisible("cp")}
                data-testid="cliente-form-cp"
                placeholder="28001"
              />
            </Campo>
            <Campo label="Notas internas" error={errorVisible("notas")} full>
              <Textarea
                value={form.notas}
                onChange={(e) =>
                  setForm({
                    ...form,
                    notas: e.target.value.slice(0, LIMITES.notasMax),
                  })
                }
                onBlur={() => marcarTouched("notas")}
                rows={2}
                maxLength={LIMITES.notasMax}
                aria-invalid={!!errorVisible("notas")}
                data-testid="cliente-form-notas"
              />
              <div className="text-[11px] text-slate-400 mt-1 text-right">
                {(form.notas || "").length}/{LIMITES.notasMax}
              </div>
            </Campo>
          </div>

          {defs.length > 0 ? (
            <div className="pt-3 border-t border-slate-200">
              <h3 className="font-semibold text-slate-800 text-sm mb-2">
                Campos personalizados
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {defs.map((d) => (
                  <CampoCustom
                    key={d.key}
                    def={d}
                    value={form.customFields?.[d.key]}
                    onChange={(v) => setCustom(d.key, v)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {touched.__submit && !valido ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              Revisa los campos marcados antes de guardar.
            </div>
          ) : null}
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-300">
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={enviando}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300"
              data-testid="cliente-form-guardar"
            >
              {enviando ? "Guardando…" : "Guardar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CampoCustom({ def, value, onChange }) {
  const label = `${def.label}${def.required ? " *" : ""}`;
  const tid = `cliente-form-custom-${def.key}`;

  if (def.type === "longtext") {
    return (
      <Campo label={label} full>
        <Textarea
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          data-testid={tid}
        />
      </Campo>
    );
  }
  if (def.type === "number") {
    return (
      <Campo label={label}>
        <Input
          type="number"
          value={value ?? ""}
          onChange={(e) =>
            onChange(e.target.value === "" ? null : Number(e.target.value))
          }
          data-testid={tid}
        />
      </Campo>
    );
  }
  if (def.type === "boolean") {
    return (
      <Campo label={label}>
        <div className="flex items-center gap-2 pt-1.5">
          <Switch
            checked={!!value}
            onCheckedChange={onChange}
            data-testid={tid}
          />
          <span className="text-sm text-slate-600">{value ? "Sí" : "No"}</span>
        </div>
      </Campo>
    );
  }
  if (def.type === "date") {
    const dateValue = (() => {
      if (!value) return "";
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
    })();
    return (
      <Campo label={label}>
        <Input
          type="date"
          value={dateValue}
          onChange={(e) => onChange(e.target.value || null)}
          data-testid={tid}
        />
      </Campo>
    );
  }
  if (def.type === "enum") {
    return (
      <Campo label={label}>
        <Select value={value ?? ""} onValueChange={(v) => onChange(v || null)}>
          <SelectTrigger data-testid={tid}>
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            {(def.enumValues || []).map((v) => (
              <SelectItem key={v} value={v}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Campo>
    );
  }
  return (
    <Campo label={label}>
      <Input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={tid}
      />
    </Campo>
  );
}

function Campo({ label, children, full, error }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1.5 block">
        {label}
      </Label>
      <div className={error ? "[&_input]:border-red-400 [&_textarea]:border-red-400 [&_input]:focus-visible:ring-red-400 [&_textarea]:focus-visible:ring-red-400" : ""}>
        {children}
      </div>
      {error ? (
        <p className="text-[11px] text-red-600 mt-1" role="alert">{error}</p>
      ) : null}
    </div>
  );
}

function DefinicionModal({ open, defActual, onClose, onSave }) {
  const editando = !!defActual;
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [label, setLabel] = useState("");
  const [type, setType] = useState("string");
  const [required, setRequired] = useState(false);
  const [order, setOrder] = useState(0);
  const [enumValues, setEnumValues] = useState([]);
  const [enumInput, setEnumInput] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (defActual) {
      setKey(defActual.key);
      setKeyTouched(true);
      setLabel(defActual.label);
      setType(defActual.type);
      setRequired(!!defActual.required);
      setOrder(Number(defActual.order) || 0);
      setEnumValues(defActual.enumValues || []);
    } else {
      setKey("");
      setKeyTouched(false);
      setLabel("");
      setType("string");
      setRequired(false);
      setOrder(0);
      setEnumValues([]);
    }
    setEnumInput("");
  }, [open, defActual]);

  const onLabelChange = (v) => {
    setLabel(v);
    if (!editando && !keyTouched) {
      setKey(slugifyKey(v));
    }
  };

  const valido =
    label.trim().length > 0 &&
    /^[a-z][a-z0-9_]{0,39}$/.test(key) &&
    (type !== "enum" || enumValues.length > 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!valido || enviando) return;
    setEnviando(true);
    try {
      const payload = {
        key,
        label: label.trim(),
        type,
        required,
        order,
        enumValues: type === "enum" ? enumValues : undefined,
      };
      await onSave(defActual?.key || null, payload);
    } finally {
      setEnviando(false);
    }
  };

  const addEnumValue = () => {
    const v = enumInput.trim();
    if (!v) return;
    if (enumValues.includes(v)) return;
    setEnumValues((vs) => [...vs, v]);
    setEnumInput("");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="campo-modal">
        <DialogHeader>
          <DialogTitle>
            {editando ? "Editar campo personalizado" : "Nuevo campo personalizado"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Campo label="Nombre visible *">
            <Input
              value={label}
              onChange={(e) => onLabelChange(e.target.value)}
              placeholder="Ej: Tipo de cliente"
              required
              autoFocus
              data-testid="campo-form-label"
            />
          </Campo>
          <Campo label="Identificador (key) *">
            <Input
              value={key}
              onChange={(e) => {
                setKey(slugifyKey(e.target.value));
                setKeyTouched(true);
              }}
              placeholder="tipo_cliente"
              disabled={editando}
              required
              data-testid="campo-form-key"
            />
            <p className="text-xs text-slate-400 mt-1">
              Solo minúsculas, números y guiones bajos. No se puede cambiar después.
            </p>
          </Campo>
          <div className="grid grid-cols-2 gap-4">
            <Campo label="Tipo *">
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="campo-form-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {FIELD_TYPE_LABEL[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>
            <Campo label="Orden">
              <Input
                type="number"
                value={order}
                onChange={(e) => setOrder(Number(e.target.value) || 0)}
                data-testid="campo-form-order"
              />
            </Campo>
          </div>
          {type === "enum" ? (
            <Campo label="Opciones *" full>
              <div className="flex gap-2">
                <Input
                  value={enumInput}
                  onChange={(e) => setEnumInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addEnumValue();
                    }
                  }}
                  placeholder="Añadir opción y pulsar Enter"
                  data-testid="campo-form-enum-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addEnumValue}
                  className="border-slate-300"
                >
                  Añadir
                </Button>
              </div>
              {enumValues.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {enumValues.map((v) => (
                    <span
                      key={v}
                      className="inline-flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-full pl-3 pr-1 py-0.5 text-xs"
                    >
                      {v}
                      <button
                        type="button"
                        onClick={() =>
                          setEnumValues((vs) => vs.filter((x) => x !== v))
                        }
                        className="p-0.5 rounded-full text-slate-500 hover:text-red-600 hover:bg-red-50"
                        aria-label={`Quitar ${v}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}
            </Campo>
          ) : null}
          <div className="flex items-center gap-2">
            <Switch
              id="campo-required"
              checked={required}
              onCheckedChange={setRequired}
              data-testid="campo-form-required"
            />
            <Label htmlFor="campo-required" className="text-sm text-slate-700">
              Campo obligatorio
            </Label>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-300">
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!valido || enviando}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300"
              data-testid="campo-form-guardar"
            >
              {enviando ? "Guardando…" : "Guardar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmEliminar({ open, title, descripcion, onCancel, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">{descripcion}</p>
        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
          <Button variant="outline" onClick={onCancel} className="border-slate-300">
            Cancelar
          </Button>
          <Button
            onClick={onConfirm}
            className="bg-red-600 hover:bg-red-700 gap-2"
          >
            <Trash2 className="w-4 h-4" /> Eliminar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
